import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { camelCase_to_Sentence, pluralReadableTypeName, readableTypeName, toTitleCase } from '@iotv/core-services';
import { NotificationSubscription, SubscribedStatus } from '@iotv/datamodel';
import { BranchingQueryDefinition, DeliveryType, edgeTypeMatrix, IncidentTypeNames, RuleToAlertIncidentMap, RuleTypes, } from '@iotv/datamodel-core';
import { AdjacentType, getZombieInstanceFromPrimaryKey, isValidBusinessObject } from '@iotv/iotv-v3-types';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import MailIcon from '@mui/icons-material/Mail';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import SmsIcon from '@mui/icons-material/Sms';
import SnoozeIcon from '@mui/icons-material/Snooze';
import { Autocomplete, Box, Button, FormControl, FormGroup, Grid, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { isAdminRole } from '../../../helpers/access';
import { singularArticle } from '../../../helpers/format';
import { DataGridRadioColumn } from '../../../util/DataGrid/DataGridHelpers';
import { AutosizedTextField } from '../../common/AutosizedTextField';
import { SingleEntityAutoQuery } from '../../generic/EntityAutoQuery';
import { SubscriptionLineItem } from './SubscriptionLineItem';
const debug = process.env.REACT_APP_DEBUG && false;
/**
 * Fetches an object from the database by ther `pk`, and returns their name
 *
 * https://github.com/mui/mui-x/issues/5705#issuecomment-1206268762
 *
 * @param pk the object to fetch
 * @returns the name of the object
 */
const pkToName = (pk) => {
    if (pk === undefined || pk.length === 0)
        return '';
    const [name, setName] = useState();
    if (pk?.includes(':'))
        ConsumerDao.getExact(getZombieInstanceFromPrimaryKey(pk)).then(({ err, data }) => {
            if (err && !data)
                return setName(`(Error: ${err.name})`);
            if (data?.Count && 0 < data?.Count && data.Items)
                return setName(data.Items[0].name);
            setName('(Not found)');
        });
    else
        return pk;
    return name ?? 'Loading...';
};
const columnLayout = {
    display: 'flex',
    resizable: false,
    sortable: false,
    disableColumnMenu: true,
};
const subscriptionColumns = [
    {
        field: 'subscriptionState',
        headerName: 'Status',
        ...columnLayout,
        width: 65,
        valueGetter: (value) => value?.status,
        renderCell: (params) => {
            let StatusIcon = undefined;
            switch (params.value) {
                case SubscribedStatus.ACTIVE:
                    StatusIcon = _jsx(CheckIcon, { color: "success" });
                    break;
                case SubscribedStatus.POSTPONED:
                    StatusIcon = _jsx(SnoozeIcon, { color: "warning" });
                    break;
                case SubscribedStatus.SUSPENDED:
                    StatusIcon = _jsx(DoNotDisturbIcon, { color: "error" });
                    break;
            }
            return _jsx("div", { style: { flex: 1, display: 'flex', justifyContent: 'center' }, children: StatusIcon });
        },
    },
    {
        field: 'incidentType',
        headerName: 'Incident Type',
        ...columnLayout,
        minWidth: 180,
        valueFormatter: (value) => (value ? (IncidentTypeNames[value] ?? value) : value),
    },
    {
        field: 'incidentSubtype',
        headerName: 'Ruleset',
        minWidth: 150,
        renderCell: (params) => pkToName(params.value),
    },
    {
        field: 'incidentProducingTypeId',
        headerName: 'Trigger type',
        ...columnLayout,
        valueGetter: (value, row) => row.incidentEntityOrGroupingSk === `${row.incidentProducingTypeId}:*`
            ? `All ${pluralReadableTypeName(value)}`
            : row.incidentProducingTypeId === 'Group'
                ? 'Group'
                : `${toTitleCase(singularArticle(readableTypeName(value)))} ${readableTypeName(value)}`,
    },
    {
        field: 'incidentEntityOrGroupingName',
        headerName: 'Trigger entity',
        minWidth: 180,
        ...columnLayout,
    },
    {
        field: 'filter',
        headerName: 'Filter',
        ...columnLayout,
        minWidth: 180,
        renderCell: (params) => {
            const filter = params.value;
            const severities = filter?.severities ?? [];
            const statuses = filter?.statuses ?? [];
            const events = filter?.onActions ?? [];
            const filters = [...severities, ...statuses, ...events];
            return filters.length === 0 ? 'No filters' : filters.map((f) => toTitleCase(camelCase_to_Sentence(f))).join(', ');
        },
    },
    {
        field: 'deliveryPreferences',
        headerName: 'Delivery',
        ...columnLayout,
        flex: 1,
        renderCell: ({ value }) => {
            const deliveries = value ?? [];
            if (!deliveries.length)
                return _jsx(Typography, { children: "No deliveries" });
            return (_jsx("div", { style: { display: 'flex', gap: 16, flexWrap: 'nowrap' }, children: deliveries.map((delivery) => {
                    let deliveryIcon = undefined;
                    switch (delivery.deliveryType) {
                        case DeliveryType.EMAIL:
                            deliveryIcon = _jsx(MailIcon, {});
                            break;
                        case DeliveryType.SMS:
                            deliveryIcon = _jsx(SmsIcon, {});
                            break;
                        case DeliveryType.MOBILE_PUSH:
                            deliveryIcon = _jsx(SmartphoneIcon, {});
                            break;
                    }
                    return delivery.recipients
                        .map((recipient) => {
                        return (_jsxs("div", { style: { display: 'flex', gap: 4 }, children: [_jsx(Grid, { sx: { display: 'flex' }, children: deliveryIcon }, 'icon'), _jsx(Grid, { sx: { alignContent: 'center' }, children: recipient.name }, 'name')] }, recipient.id));
                    })
                        .concat(delivery.destinations.map((destination) => {
                        return (_jsxs("div", { style: { display: 'flex', gap: 4 }, children: [_jsx(Grid, { sx: { display: 'flex' }, children: deliveryIcon }, 'icon'), _jsx(Grid, { sx: { alignContent: 'center' }, children: destination }, 'name')] }, destination));
                    }));
                }) }));
        },
    },
];
const columns = [DataGridRadioColumn, ...subscriptionColumns];
export const SubscriptionsTab = ({ contextCustomer, user, userSelectedGroup, userFunctions }) => {
    const isAdmin = isAdminRole(userSelectedGroup);
    const [loading, setLoading] = useState(false);
    const [subscriptions, setSubscriptions] = useState([]);
    const [selectedSubscription, setSelectedSubscription] = useState(undefined);
    // row selection
    const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
    useEffect(() => {
        const selectedIds = 'ids' in rowSelectionModel ? Array.from(rowSelectionModel.ids) : rowSelectionModel;
        const found = subscriptions.find((s) => s.id === selectedIds[0]);
        // deep copy needed to avoid escalation config quirkiness
        setSelectedSubscription(found ? structuredClone(found) : undefined);
    }, [rowSelectionModel, subscriptions]);
    const [selectedUser, setSelectedUser] = useState(user ?? null);
    const [triggerType, setTriggerType] = useState(undefined);
    const [subscriptionState, setSubscriptionState] = useState(undefined);
    const getItems = async () => {
        if (selectedUser === null)
            return [];
        if (user === undefined)
            return [];
        const contextObject = isAdmin ? selectedUser : user;
        const { err, data } = await ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [contextObject],
            query: new BranchingQueryDefinition({
                id: contextObject.sk,
                path: [
                    {
                        objectTypeId: 'NotificationSubscription',
                        adjacencyType: AdjacentType.CHILD,
                        edgeTypeId: edgeTypeMatrix.User.NotificationSubscription.User_has_NotificationSubscription.edgeTypeId,
                    },
                ],
            }),
        });
        if (err)
            console.warn(err);
        if (data)
            return data
                .getNodesOfType('NotificationSubscription')
                .map((vob) => new NotificationSubscription(vob))
                .filter((item) => item !== undefined)
                .toSorted((a, b) => (a.id < b.id ? -1 : 0))
                .filter((item) => (triggerType ? Object.values(RuleToAlertIncidentMap[triggerType]).includes(item.incidentType) : item))
                .filter((item) => (subscriptionState ? item.subscriptionState.status === subscriptionState : item));
        return [];
    };
    const fetch = async () => {
        setLoading(true);
        setSubscriptions(await getItems());
        setLoading(false);
    };
    useEffect(() => {
        if (contextCustomer && selectedUser)
            fetch();
    }, [contextCustomer?.sk, isAdmin, selectedUser?.sk, user?.sk, triggerType, subscriptionState]);
    const handleAdd = () => {
        const newSubscription = new NotificationSubscription({
            id: v4(),
            deliveryPreferences: [],
            filter: {
                severities: [],
                statuses: [],
                onActions: [],
            },
            incidentEntityOrGroupingSk: undefined,
            incidentInitiatorSk: undefined,
            incidentProducingTypeId: undefined,
            incidentType: undefined,
            incidentSubtype: undefined,
            subscriptionState: {
                status: SubscribedStatus.ACTIVE,
                reapplyAfterEvent: undefined,
                reapplyAfterDateMs: undefined,
            },
            type: 'NotificationSubscription',
        });
        setSelectedSubscription(newSubscription);
    };
    const updateSubscription = (subscription) => {
        const index = subscriptions.findIndex((subscription) => subscription.id === selectedSubscription?.id);
        if (index === -1) {
            if (subscription) {
                setSubscriptions((s) => {
                    return [...s, subscription].toSorted((a, b) => (a.id < b.id ? -1 : 0));
                });
                setSelectedSubscription(subscription);
                setRowSelectionModel({ type: 'include', ids: new Set([subscription.id]) });
            }
            else {
                setSelectedSubscription(undefined);
            }
        }
        else {
            setSubscriptions((s) => {
                if (subscription)
                    return s.toSpliced(index, 1, subscription);
                else
                    return s.toSpliced(index, 1);
            });
        }
    };
    return (_jsxs(Stack, { minHeight: '40cqh', marginBlockStart: isAdmin ? 1 : triggerType ? 1 : 0, gap: 1, sx: { transition: 'margin-block-start 0.15s ease-in-out' }, children: [_jsx(Stack, { direction: 'row', children: _jsxs(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: 'repeat(1, auto)', md: 'repeat(2, auto)' }, gap: 1, alignItems: 'center' }, children: [isAdmin && (_jsxs(_Fragment, { children: [_jsx(Typography, { children: "View subscriptions for" }), _jsx(SingleEntityAutoQuery, { contextObject: contextCustomer, entityType: "User", currentEntity: selectedUser, setCurrentEntity: (u) => u && isValidBusinessObject(u) && setSelectedUser(u), label: "User", fullWidth: true })] })), _jsx(Typography, { children: "Filter by trigger type" }), _jsx(Autocomplete, { freeSolo: false, size: "small", fullWidth: true, multiple: false, value: triggerType ?? null, options: Object.values(RuleTypes), getOptionLabel: (option) => toTitleCase(camelCase_to_Sentence(option)), onChange: (_, value) => setTriggerType(value ?? undefined), renderInput: (inputProps) => _jsx(AutosizedTextField, { ...inputProps, label: 'Rule Type' }) }), _jsx(Typography, { children: "Filter by state" }), _jsx(Autocomplete, { freeSolo: false, size: "small", fullWidth: true, multiple: false, value: subscriptionState ?? null, options: Object.values(SubscribedStatus), getOptionLabel: (option) => toTitleCase(camelCase_to_Sentence(option)), onChange: (_, value) => setSubscriptionState(value ?? undefined), renderInput: (inputProps) => _jsx(AutosizedTextField, { ...inputProps, label: 'Status' }) })] }) }), _jsx(DataGrid, { loading: loading, rows: loading ? [] : subscriptions, columns: columns, checkboxSelection: true, pageSizeOptions: [10], hideFooter: subscriptions.length <= 10, hideFooterPagination: subscriptions.length <= 10, hideFooterSelectedRowCount: true, disableMultipleRowSelection: true, rowSelectionModel: rowSelectionModel, onRowSelectionModelChange: setRowSelectionModel }), selectedSubscription && contextCustomer && selectedUser && userSelectedGroup ? (_jsx(SubscriptionLineItem, { contextCustomer,
                contextUser: selectedUser,
                userSelectedGroup,
                subscription: selectedSubscription,
                userFunctions,
                updateInParent: updateSubscription }, selectedSubscription.id)) : (_jsx(Stack, { direction: 'row', children: _jsx(FormGroup, { children: _jsx(FormControl, { children: _jsx(Button, { size: 'small',
                            startIcon: _jsx(AddIcon, {}),
                            variant: 'contained',
                            color: 'primary',
                            onClick: handleAdd, children: "Add Subscription" }) }) }) }))] }, 'ConfigurationTabMainGrid'));
};
